/**
 * SportsTournamentDetail — /sports/tournaments/:slug
 *
 * Full public page for a tournament: description, schedule, venue,
 * rules, photos and embedded videos.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Trophy, Calendar, MapPin, Phone, Mail, Loader2, FileText, Video } from "lucide-react";
import { http } from "@/lib/api";
import SportsCategoryNav from "@/components/SportsCategoryNav";
import VideoEmbed from "@/components/VideoEmbed";

export default function SportsTournamentDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    http.get(`/sports/tournaments/${slug}`).then(r => setT(r.data)).catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) {
    return (
      <>
        <SportsCategoryNav />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1 className="font-display text-2xl mb-2">Tournament not found</h1>
          <p className="opacity-70">{err}</p>
          <Link to="/sports/tournaments" className="wofy-link inline-block mt-4">← Back to tournaments</Link>
        </div>
      </>
    );
  }
  if (!t) {
    return (
      <>
        <SportsCategoryNav />
        <div className="max-w-3xl mx-auto px-4 py-12 inline-flex items-center gap-2 opacity-60">
          <Loader2 className="w-4 h-4 animate-spin" />Loading…
        </div>
      </>
    );
  }

  return (
    <>
      <SportsCategoryNav />
      <article className="max-w-4xl mx-auto px-4 py-8" data-testid="tournament-detail">
        {t.cover_image_url && (
          <div className="w-full h-64 sm:h-80 rounded-xl overflow-hidden mb-6 bg-black/5"
               style={{ backgroundImage: `url(${t.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}

        <div className="overline mb-2">
          <Trophy className="w-3 h-3 inline mr-1" />{t.sport}{t.format ? ` · ${t.format}` : ""}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">{t.name}</h1>

        <div className="flex flex-wrap gap-4 text-sm opacity-80 mb-6">
          {t.start_date && (
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />
              {t.start_date}{t.end_date && t.end_date !== t.start_date ? ` – ${t.end_date}` : ""}
            </span>
          )}
          {(t.venue_name || t.city) && (
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />
              {[t.venue_name, t.city, t.state, t.country].filter(Boolean).join(", ")}
            </span>
          )}
          {t.entry_fee_amount != null && (
            <span className="inline-flex items-center gap-1">
              Entry fee · {t.entry_fee_currency || "USD"} {t.entry_fee_amount}
            </span>
          )}
        </div>

        {t.description && (
          <section className="prose max-w-none mb-8">
            <p className="whitespace-pre-line leading-relaxed opacity-90">{t.description}</p>
          </section>
        )}

        {/* Categories */}
        {t.categories?.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-2">Categories</h2>
            <div className="flex flex-wrap gap-2">
              {t.categories.map((c, i) => (
                <span key={i} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>{c}</span>
              ))}
            </div>
          </section>
        )}

        {/* Rules */}
        {t.rules && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-2">
              <FileText className="w-5 h-5" />Rules
            </h2>
            <p className="whitespace-pre-line text-sm opacity-90 leading-relaxed">{t.rules}</p>
          </section>
        )}

        {/* Videos */}
        {t.video_links?.length > 0 && (
          <section className="mb-8" data-testid="tournament-videos">
            <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2">
              <Video className="w-5 h-5" />Videos
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {t.video_links.map((v, i) => (
                <VideoEmbed key={i} video={v} />
              ))}
            </div>
          </section>
        )}

        {/* Photos */}
        {t.photo_urls?.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-3">Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {t.photo_urls.map((p, i) => (
                <img key={i} src={p} alt="" className="w-full aspect-square object-cover rounded-lg" />
              ))}
            </div>
          </section>
        )}

        {/* Organizer contact */}
        {(t.contact_email || t.contact_phone || t.organizer_name) && (
          <section className="wofy-card !p-4">
            <div className="overline mb-1">ORGANIZER</div>
            {t.organizer_name && <div className="font-medium">{t.organizer_name}</div>}
            <div className="text-sm opacity-80 mt-1 flex flex-wrap gap-3">
              {t.contact_email && <a href={`mailto:${t.contact_email}`} className="inline-flex items-center gap-1 wofy-link"><Mail className="w-3.5 h-3.5" />{t.contact_email}</a>}
              {t.contact_phone && <a href={`tel:${t.contact_phone}`} className="inline-flex items-center gap-1 wofy-link"><Phone className="w-3.5 h-3.5" />{t.contact_phone}</a>}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
